import { feeDecisionResponseSchema, feesToConfirmResponseSchema, type FeeDecisionResponse, type FeesToConfirmResponse } from "@teamna/shared";
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { api } from "../api/client.ts";
import { queryKeys } from "./keys.ts";

/**
 * No-show and late-cancel fees (Pilates studio shape I2, migration 0078). The owner and admins see
 * the fees that wait, and send (a one-off payment request with a card link) or waive each one.
 */

const base = (businessId: string) => `/owner/businesses/${businessId}/fees`;

/** GET /v1/owner/businesses/:id/fees */
export function useFeesToConfirm(businessId: string | null): UseQueryResult<FeesToConfirmResponse> {
  return useQuery({
    queryKey: queryKeys.fees(businessId ?? "none"),
    queryFn: () => api.get(base(businessId!), feesToConfirmResponseSchema),
    enabled: Boolean(businessId),
    staleTime: 30 * 1000,
  });
}

export type FeeAction = { feeId: string; decision: "send" | "waive" };

/** Send or Waive. The fee leaves the list and the owner To do count goes down. */
export function useDecideFee(businessId: string): UseMutationResult<FeeDecisionResponse, unknown, FeeAction> {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.fees(businessId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.todo.owner(businessId) });
  };
  return useMutation({
    mutationFn: ({ feeId, decision }) => api.post(`${base(businessId)}/${feeId}/${decision}`, undefined, feeDecisionResponseSchema),
    onSuccess: refresh,
    // The mark changed, or someone decided it first: read the list again so the screen shows why.
    onError: refresh,
  });
}
